import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import * as printPdfDealSelectors from '@makemydeal/dr-dash-store/selectors/printPdfDealSelectors';

export interface PrintScenarioSelectionState {
    selectableScopeSummaries: ReturnType<typeof printPdfDealSelectors.getPrintableScopeSummaries>;
    selectedScopeIds: string[];
    setSelectedScopeIds: (ids: string[]) => void;
    showScenarioSelector: boolean;
    hasSelectedScopes: boolean;
    getSelectedScopeIdsForAction: () => string[] | undefined;
    resetSelection: () => void;
}

export const usePrintScenarioSelection = (isFourSquare?: boolean): PrintScenarioSelectionState => {
    const scopeSummaries = useSelector(printPdfDealSelectors.getPrintableScopeSummaries);

    const selectableScopeSummaries = useMemo(
        () =>
            isFourSquare
                ? scopeSummaries.filter((s) => s.offerCategory !== 'balloon' && s.offerCategory !== 'flexbuy')
                : scopeSummaries,
        [isFourSquare, scopeSummaries]
    );

    const selectableScopeIds = useMemo(() => selectableScopeSummaries.map((s) => s.scopeId), [selectableScopeSummaries]);
    // Membership key so the selection only re-syncs when scopes appear/disappear, not when their payment details change
    const selectableScopeIdsKey = selectableScopeIds.join(',');

    const [selectedScopeIds, setSelectedScopeIds] = useState<string[]>(selectableScopeIds);
    const showScenarioSelector = selectableScopeSummaries.length > 1 || !isFourSquare;
    const hasSelectedScopes = selectedScopeIds.length > 0;

    useEffect(() => {
        setSelectedScopeIds(selectableScopeIds);
    }, [selectableScopeIdsKey]);

    const getSelectedScopeIdsForAction = useCallback(() => {
        // Nothing printable known yet (scope data still loading) - print every scope instead of narrowing to none
        if (selectableScopeIds.length === 0) return undefined;
        return selectedScopeIds;
    }, [selectableScopeIdsKey, selectedScopeIds]);

    const resetSelection = useCallback(() => {
        setSelectedScopeIds(selectableScopeIds);
    }, [selectableScopeIdsKey]);

    return {
        selectableScopeSummaries,
        selectedScopeIds,
        setSelectedScopeIds,
        showScenarioSelector,
        hasSelectedScopes,
        getSelectedScopeIdsForAction,
        resetSelection
    };
};
